import axios from "axios";
import { baseurl } from "../Basurl/Baseurl";

/**
 * Clean API base URL: ensuring https://sisccltd.com/omca_crm/api/guesthouse-services/
 */
const getCleanBase = () => {
  const root = baseurl.endsWith("/") ? baseurl : `${baseurl}/`;
  return `${root}guesthouse-services/`;
};

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("token") || "";
  const headers = {
    Authorization: token ? `Bearer ${token}` : "",
  };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

// Fallback in-memory / sessionStorage store for offline or pre-deployment testing
const STORAGE_KEY = "omca_gh_services_cache";

const getCachedRecords = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn("Error reading GH cache:", e);
  }
  return [
    {
      _id: "66eab1234567890abcdef123",
      guestHouseServiceId: "GHS-48291",
      patientId: "P-10023",
      patient_name: "Amina Rahman",
      treatment_id: "TRT-0042",
      guestHouseName: "Lotus Residency",
      roomNumber: "204",
      stayDateRange: {
        from: "2026-09-20T00:00:00.000Z",
        to: "2026-09-28T00:00:00.000Z",
      },
      stayDateRangeFrom: "2026-09-20",
      stayDateRangeTo: "2026-09-28",
      services: [
        {
          _id: "66eab1234567890abcdef456",
          serviceId: "Svc-1001",
          serviceName: "Laundry Service",
          price: 500,
          quantity: 2,
          final_price: 1000,
          duration: "One-Time",
          status: "In Progress",
          description: "2 loads of laundry",
        },
        {
          _id: "66eab1234567890abcdef457",
          serviceId: "Svc-1002",
          serviceName: "Special Nursing Care",
          price: 1200,
          quantity: 3,
          discount_percentage: 10,
          final_price: 3240,
          duration: "Day",
          status: "Active",
          description: "Daily 2 hours nursing assistance",
        },
      ],
      payments: [
        {
          _id: "pay-101",
          paid_amount: 1500,
          paymentMethod: "UPI",
          payment_Date: "2026-09-21",
          status: "Approved",
          notes: "Advance deposit via GPay",
          receipts: [],
        },
      ],
      totalAmount: 4240,
      final_amount: 4240,
      paidAmount: 1500,
      dueAmount: 2740,
      status: "Active",
      notes: "Patient requested vegetarian food plan",
      createdAt: "2026-09-18T06:00:00.000Z",
    },
    {
      _id: "66eab1234567890abcdef124",
      guestHouseServiceId: "GHS-48292",
      patientId: "P-10045",
      patient_name: "Rahul Verma",
      treatment_id: "TRT-0089",
      guestHouseName: "Green Park Suites",
      roomNumber: "102",
      stayDateRange: {
        from: "2026-09-15T00:00:00.000Z",
        to: "2026-09-25T00:00:00.000Z",
      },
      stayDateRangeFrom: "2026-09-15",
      stayDateRangeTo: "2026-09-25",
      services: [
        {
          _id: "66eab1234567890abcdef458",
          serviceId: "Svc-2005",
          serviceName: "Airport Drop",
          price: 1500,
          quantity: 1,
          final_price: 1500,
          duration: "One-Time",
          status: "Completed",
          description: "Sedan airport transfer",
        },
      ],
      payments: [
        {
          _id: "pay-102",
          paid_amount: 1500,
          paymentMethod: "Cash",
          payment_Date: "2026-09-16",
          status: "Approved",
          notes: "Full payment received",
          receipts: [],
        },
      ],
      totalAmount: 1500,
      final_amount: 1500,
      paidAmount: 1500,
      dueAmount: 0,
      status: "Completed",
      notes: "Checkout completed smoothly",
      createdAt: "2026-09-15T08:30:00.000Z",
    },
  ];
};

const saveCachedRecords = (records) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch (e) {
    console.warn("Error saving GH cache:", e);
  }
};

/**
 * 1. GET /api/guesthouse-services/dashboard-stats
 */
export const fetchGHDashboardStats = async () => {
  try {
    const res = await axios.get(`${getCleanBase()}dashboard-stats`, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data.data;
    }
  } catch (err) {
    console.warn("GHDashboardStats API call failed, using fallback:", err.message);
  }

  // Fallback calculation from cache
  const records = getCachedRecords();
  const totalRecords = records.length;
  const activeRecords = records.filter((r) => r.status === "Active").length;
  const completedRecords = records.filter((r) => r.status === "Completed").length;
  const totalBilling = records.reduce((sum, r) => sum + (Number(r.final_amount || r.totalAmount) || 0), 0);
  const totalPaid = records.reduce((sum, r) => sum + (Number(r.paidAmount) || 0), 0);
  const totalDue = records.reduce((sum, r) => sum + (Number(r.dueAmount) || 0), 0);
  const totalServicesCount = records.reduce((sum, r) => sum + (r.services?.length || 0), 0);

  return {
    totalRecords,
    activeRecords,
    completedRecords,
    totalBilling,
    totalPaid,
    totalDue,
    totalServicesCount,
  };
};

/**
 * 2. GET /api/guesthouse-services/list
 * Supports: page, limit, search, patientId, status, guestHouseName, dateFrom, dateTo
 */
export const fetchGHServicesList = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
        query.append(key, params[key]);
      }
    });

    const res = await axios.get(`${getCleanBase()}list?${query.toString()}`, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("GHServicesList API call failed, using fallback:", err.message);
  }

  // Fallback filtering
  let records = getCachedRecords();

  if (params.search) {
    const s = String(params.search).toLowerCase();
    records = records.filter((r) => {
      const patientIdMatch = String(r.patientId || "").toLowerCase().includes(s);
      const nameMatch = String(r.patient_name || "").toLowerCase().includes(s);
      const ghMatch = String(r.guestHouseName || "").toLowerCase().includes(s);
      const svcMatch = r.services?.some((item) =>
        String(item.serviceName || "").toLowerCase().includes(s)
      );
      const codeMatch = String(r.guestHouseServiceId || "").toLowerCase().includes(s);
      return patientIdMatch || nameMatch || ghMatch || svcMatch || codeMatch;
    });
  }

  if (params.status && params.status !== "ALL" && params.status !== "All") {
    records = records.filter(
      (r) => String(r.status).toLowerCase() === String(params.status).toLowerCase()
    );
  }

  if (params.guestHouseName && params.guestHouseName !== "ALL") {
    records = records.filter(
      (r) =>
        String(r.guestHouseName).toLowerCase() === String(params.guestHouseName).toLowerCase()
    );
  }

  if (params.patientId) {
    records = records.filter(
      (r) => String(r.patientId || "").toLowerCase() === String(params.patientId).toLowerCase()
    );
  }

  const totalCount = records.length;
  const page = Number(params.page) || 1;
  const limit = params.limit === "all" ? totalCount : Number(params.limit) || 10;
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const start = (page - 1) * limit;
  const paginatedData = limit === totalCount ? records : records.slice(start, start + limit);

  return {
    success: true,
    message: "Guest house service records fetched successfully",
    totalCount,
    currentPage: page,
    totalPages,
    summary: {
      totalBilling: records.reduce((acc, r) => acc + (Number(r.final_amount || r.totalAmount) || 0), 0),
      totalPaid: records.reduce((acc, r) => acc + (Number(r.paidAmount) || 0), 0),
      totalDue: records.reduce((acc, r) => acc + (Number(r.dueAmount) || 0), 0),
      totalServicesCount: records.reduce((acc, r) => acc + (r.services?.length || 0), 0),
    },
    data: paginatedData,
  };
};

/**
 * 3. GET /api/guesthouse-services/details/:id
 */
export const fetchGHServiceDetails = async (id) => {
  try {
    const res = await axios.get(`${getCleanBase()}details/${id}`, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data.data;
    }
  } catch (err) {
    console.warn("GHServiceDetails API call failed, using fallback:", err.message);
  }

  const records = getCachedRecords();
  const matched = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (matched) return matched;
  return records[0] || null;
};

/**
 * 4. GET /api/guesthouse-services/patient/:patientId
 */
export const fetchGHPatientServices = async (patientId) => {
  try {
    const res = await axios.get(`${getCleanBase()}patient/${patientId}`, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return Array.isArray(res.data.data) ? res.data.data : [res.data.data];
    }
  } catch (err) {
    console.warn("GHPatientServices API call failed, using fallback:", err.message);
  }

  const records = getCachedRecords();
  const cleanId = String(patientId || "").toLowerCase();
  return records.filter(
    (r) =>
      String(r.patientId || "").toLowerCase() === cleanId ||
      String(r.patient_name || "").toLowerCase().includes(cleanId)
  );
};

/**
 * 5. POST /api/guesthouse-services/create
 */
export const createGHServiceRecord = async (payload) => {
  const isMultipart = payload instanceof FormData;
  try {
    const res = await axios.post(`${getCleanBase()}create`, payload, {
      headers: getAuthHeaders(isMultipart),
      timeout: 10000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("createGHServiceRecord API call failed, saving to fallback:", err.message);
  }

  // Fallback create
  const records = getCachedRecords();
  const data = isMultipart ? Object.fromEntries(payload.entries()) : payload;

  const rawServices = Array.isArray(data.services) ? data.services : [];
  let totalAmount = 0;
  const processedServices = rawServices.map((svc, idx) => {
    const price = Number(svc.price) || 0;
    const qty = Number(svc.quantity) || 1;
    const discount = Number(svc.discount_percentage) || 0;
    const base = price * qty;
    const final_price = discount ? base - (base * discount) / 100 : base;
    totalAmount += final_price;
    return {
      _id: `svc-${Date.now()}-${idx}`,
      serviceId: svc.serviceId || `Svc-${1000 + idx}`,
      serviceName: svc.serviceName || "Guest Service",
      description: svc.description || "",
      price,
      quantity: qty,
      duration: svc.duration || "One-Time",
      discount_percentage: discount,
      final_price,
      status: "In Progress",
    };
  });

  const paidAmount = Number(data.initialPayment?.paid_amount) || 0;
  const dueAmount = Math.max(0, totalAmount - paidAmount);

  const newRecord = {
    _id: `ghs-${Date.now()}`,
    guestHouseServiceId: `GHS-${Math.floor(10000 + Math.random() * 90000)}`,
    patientId: data.patientId || "P-Unknown",
    patient_name: data.patient_name || data.patientName || "Patient",
    treatment_id: data.treatment_id || data.treatmentId || "-",
    guestHouseName: data.guestHouseName || "Main Guest House",
    roomNumber: data.roomNumber || "-",
    stayDateRange: {
      from: data.stayDateRangeFrom || new Date().toISOString(),
      to: data.stayDateRangeTo || new Date().toISOString(),
    },
    stayDateRangeFrom: data.stayDateRangeFrom || "",
    stayDateRangeTo: data.stayDateRangeTo || "",
    services: processedServices,
    payments: paidAmount > 0
      ? [
          {
            _id: `pay-${Date.now()}`,
            paid_amount: paidAmount,
            paymentMethod: data.initialPayment?.paymentMethod || "Cash",
            payment_Date: data.initialPayment?.payment_Date || new Date().toISOString().slice(0, 10),
            notes: data.initialPayment?.notes || "Initial advance deposit",
            status: "Approved",
          },
        ]
      : [],
    totalAmount,
    final_amount: totalAmount,
    paidAmount,
    dueAmount,
    status: "Active",
    notes: data.notes || "",
    createdAt: new Date().toISOString(),
  };

  records.unshift(newRecord);
  saveCachedRecords(records);
  return {
    success: true,
    message: "Guest house service record created successfully",
    data: newRecord,
  };
};

/**
 * 6. PUT /api/guesthouse-services/update/:id
 */
export const updateGHStayInfo = async (id, data) => {
  try {
    const res = await axios.put(`${getCleanBase()}update/${id}`, data, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("updateGHStayInfo API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const idx = records.findIndex(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (idx !== -1) {
    records[idx] = {
      ...records[idx],
      ...data,
      stayDateRange: {
        from: data.stayDateRangeFrom || records[idx].stayDateRange?.from,
        to: data.stayDateRangeTo || records[idx].stayDateRange?.to,
      },
    };
    saveCachedRecords(records);
    return { success: true, message: "Stay info updated successfully", data: records[idx] };
  }
  return { success: true, message: "Updated" };
};

/**
 * 7. POST /api/guesthouse-services/add-service/:id
 */
export const addGHMoreServices = async (id, payload) => {
  try {
    const res = await axios.post(`${getCleanBase()}add-service/${id}`, payload, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("addGHMoreServices API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec) {
    const newItems = Array.isArray(payload.services)
      ? payload.services
      : payload.service
      ? [payload.service]
      : [];

    newItems.forEach((svc, i) => {
      const price = Number(svc.price) || 0;
      const qty = Number(svc.quantity) || 1;
      const discount = Number(svc.discount_percentage) || 0;
      const base = price * qty;
      const final_price = discount ? base - (base * discount) / 100 : base;

      rec.services.push({
        _id: `svc-${Date.now()}-${i}`,
        serviceId: svc.serviceId || `Svc-${Math.floor(1000 + Math.random() * 9000)}`,
        serviceName: svc.serviceName || "Additional Service",
        description: svc.description || "",
        price,
        quantity: qty,
        duration: svc.duration || "One-Time",
        discount_percentage: discount,
        final_price,
        status: "In Progress",
      });
      rec.totalAmount = (Number(rec.totalAmount) || 0) + final_price;
      rec.final_amount = rec.totalAmount;
      rec.dueAmount = Math.max(0, rec.final_amount - (Number(rec.paidAmount) || 0));
    });
    saveCachedRecords(records);
    return { success: true, message: "Services added successfully", data: rec };
  }
  return { success: true, message: "Services added successfully" };
};

/**
 * 8. PUT /api/guesthouse-services/update-service/:id/:serviceItemId
 */
export const updateGHSingleService = async (id, serviceItemId, data) => {
  try {
    const res = await axios.put(
      `${getCleanBase()}update-service/${id}/${serviceItemId}`,
      data,
      {
        headers: getAuthHeaders(),
        timeout: 8000,
      }
    );
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("updateGHSingleService API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec && rec.services) {
    const svc = rec.services.find(
      (s) => String(s._id) === String(serviceItemId) || String(s.serviceId) === String(serviceItemId)
    );
    if (svc) {
      if (data.price !== undefined) svc.price = Number(data.price);
      if (data.quantity !== undefined) svc.quantity = Number(data.quantity);
      if (data.discount_percentage !== undefined)
        svc.discount_percentage = Number(data.discount_percentage);
      if (data.status) svc.status = data.status;
      if (data.notes) svc.notes = data.notes;

      const base = (Number(svc.price) || 0) * (Number(svc.quantity) || 1);
      svc.final_price = svc.discount_percentage
        ? base - (base * Number(svc.discount_percentage)) / 100
        : base;

      // Recalculate totals
      rec.totalAmount = rec.services.reduce((sum, s) => sum + (Number(s.final_price) || 0), 0);
      rec.final_amount = rec.totalAmount;
      rec.dueAmount = Math.max(0, rec.final_amount - (Number(rec.paidAmount) || 0));
      saveCachedRecords(records);
    }
  }
  return { success: true, message: "Service item updated successfully" };
};

/**
 * 9. DELETE /api/guesthouse-services/delete-service/:id/:serviceItemId
 */
export const deleteGHSingleService = async (id, serviceItemId) => {
  try {
    const res = await axios.delete(
      `${getCleanBase()}delete-service/${id}/${serviceItemId}`,
      {
        headers: getAuthHeaders(),
        timeout: 8000,
      }
    );
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("deleteGHSingleService API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec && rec.services) {
    rec.services = rec.services.filter(
      (s) => String(s._id) !== String(serviceItemId) && String(s.serviceId) !== String(serviceItemId)
    );
    rec.totalAmount = rec.services.reduce((sum, s) => sum + (Number(s.final_price) || 0), 0);
    rec.final_amount = rec.totalAmount;
    rec.dueAmount = Math.max(0, rec.final_amount - (Number(rec.paidAmount) || 0));
    saveCachedRecords(records);
  }
  return { success: true, message: "Service item deleted successfully" };
};

/**
 * 10. POST /api/guesthouse-services/add-payment/:id (Multipart)
 */
export const addGHPayment = async (id, formData) => {
  const isMultipart = formData instanceof FormData;
  try {
    const res = await axios.post(`${getCleanBase()}add-payment/${id}`, formData, {
      headers: getAuthHeaders(isMultipart),
      timeout: 10000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("addGHPayment API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec) {
    if (!rec.payments) rec.payments = [];
    const paid = isMultipart
      ? Number(formData.get("paid_amount")) || 0
      : Number(formData.paid_amount) || 0;
    const method = isMultipart ? formData.get("paymentMethod") : formData.paymentMethod;
    const date = isMultipart ? formData.get("payment_Date") : formData.payment_Date;
    const notes = isMultipart ? formData.get("notes") : formData.notes;

    rec.payments.push({
      _id: `pay-${Date.now()}`,
      paid_amount: paid,
      paymentMethod: method || "Cash",
      payment_Date: date || new Date().toISOString().slice(0, 10),
      notes: notes || "",
      status: "Approved",
    });
    rec.paidAmount = (Number(rec.paidAmount) || 0) + paid;
    rec.dueAmount = Math.max(0, (Number(rec.final_amount || rec.totalAmount) || 0) - rec.paidAmount);
    saveCachedRecords(records);
  }
  return { success: true, message: "Payment added successfully" };
};

/**
 * 11. PUT /api/guesthouse-services/update-payment/:id/:paymentId
 */
export const updateGHPayment = async (id, paymentId, data) => {
  const isMultipart = data instanceof FormData;
  try {
    const res = await axios.put(
      `${getCleanBase()}update-payment/${id}/${paymentId}`,
      data,
      {
        headers: getAuthHeaders(isMultipart),
        timeout: 8000,
      }
    );
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("updateGHPayment API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec && rec.payments) {
    const p = rec.payments.find((item) => String(item._id) === String(paymentId));
    if (p) {
      if (data.paid_amount !== undefined) p.paid_amount = Number(data.paid_amount);
      if (data.paymentMethod) p.paymentMethod = data.paymentMethod;
      if (data.status) p.status = data.status;
      if (data.notes) p.notes = data.notes;

      // Re-sum payments
      rec.paidAmount = rec.payments.reduce((sum, item) => sum + (Number(item.paid_amount) || 0), 0);
      rec.dueAmount = Math.max(0, (Number(rec.final_amount || rec.totalAmount) || 0) - rec.paidAmount);
      saveCachedRecords(records);
    }
  }
  return { success: true, message: "Payment updated successfully" };
};

/**
 * 12. DELETE /api/guesthouse-services/delete-payment/:id/:paymentId
 */
export const deleteGHPayment = async (id, paymentId) => {
  try {
    const res = await axios.delete(
      `${getCleanBase()}delete-payment/${id}/${paymentId}`,
      {
        headers: getAuthHeaders(),
        timeout: 8000,
      }
    );
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("deleteGHPayment API call failed, updating fallback:", err.message);
  }

  const records = getCachedRecords();
  const rec = records.find(
    (r) => String(r._id) === String(id) || String(r.guestHouseServiceId) === String(id)
  );
  if (rec && rec.payments) {
    rec.payments = rec.payments.filter((item) => String(item._id) !== String(paymentId));
    rec.paidAmount = rec.payments.reduce((sum, item) => sum + (Number(item.paid_amount) || 0), 0);
    rec.dueAmount = Math.max(0, (Number(rec.final_amount || rec.totalAmount) || 0) - rec.paidAmount);
    saveCachedRecords(records);
  }
  return { success: true, message: "Payment deleted successfully" };
};

/**
 * 13. DELETE /api/guesthouse-services/delete/:id
 */
export const deleteGHRecord = async (id) => {
  try {
    const res = await axios.delete(`${getCleanBase()}delete/${id}`, {
      headers: getAuthHeaders(),
      timeout: 8000,
    });
    if (res.data && res.data.success) {
      return res.data;
    }
  } catch (err) {
    console.warn("deleteGHRecord API call failed, updating fallback:", err.message);
  }

  let records = getCachedRecords();
  records = records.filter(
    (r) => String(r._id) !== String(id) && String(r.guestHouseServiceId) !== String(id)
  );
  saveCachedRecords(records);
  return { success: true, message: "Guest house service record deleted successfully" };
};

export const GH_STATUSES = ["Active", "Completed", "Cancelled"];
export const GH_PAYMENT_METHODS = [
  "UPI",
  "Cash",
  "Credit/Debit Card",
  "Via Net Banking",
  "foundation",
  "Internet banking",
  "GOP",
  "Other",
];
export const GH_SERVICE_DURATIONS = ["One-Time", "Day", "Week", "Month"];
